import type {
  ReferrerErrorCode,
  ReferrerErrorField,
} from "@redotech/redo-referrals-core/errors";

interface FieldErrorMapping {
  /** Form field the error is attributable to, or null for form-level errors. */
  field: ReferrerErrorField | null;
  /** User-visible message tailored to the admin form context. */
  message: string;
}

function assertNever(_x: never): never {
  throw new Error("Unexpected ReferrerErrorCode");
}

/**
 * Convert a `ReferrerError.code` from the server into a UI-ready
 * `{ field, message }` pair the create-referrer modal can route to the
 * right form input.
 *
 * The switch is exhaustive over `ReferrerErrorCode`. Adding a new error
 * subclass in `referrers/core` without updating this mapper is a compile
 * error in the `default` branch.
 */
export function mapReferrerErrorCodeToFieldError(
  code: ReferrerErrorCode,
): FieldErrorMapping {
  switch (code) {
    case "referrer.email.already_exists":
      return {
        field: "email",
        message: "An account with this email already exists.",
      };

    case "referrer.referral_code.invalid":
      return {
        field: "referralCode",
        message: "Referral code must be 3–20 characters, A–Z and 0–9 only.",
      };
    case "referrer.referral_code.reserved":
      return {
        field: "referralCode",
        message: "This referral code is reserved.",
      };
    case "referrer.referral_code.already_exists":
      return {
        field: "referralCode",
        message: "This referral code is already taken.",
      };
    case "referrer.referral_code.generation_exhausted":
      return {
        field: "referralCode",
        message:
          "Could not auto-generate a unique code. Please supply one explicitly.",
      };

    case "referrer.status.invalid_transition":
      return {
        field: "status",
        message:
          "This status change isn't allowed (terminated referrers can't be reactivated).",
      };

    // Auth-side codes that don't map to a form field. These shouldn't
    // surface from the create-referrer flow but the admin RPC layer reuses
    // the same code namespace for resend / detail flows; map them to
    // form-level errors so the same helper can serve those forms too.
    case "referrer.auth.generic":
    case "referrer.auth.magic_link_invalid":
    case "referrer.auth.magic_link_already_used":
    case "referrer.auth.referrer_not_found":
    case "referrer.auth.referrer_terminated":
    case "referrer.auth.referrer_paused":
      return {
        field: null,
        message: "Something went wrong. Please try again.",
      };

    default:
      return assertNever(code);
  }
}

/**
 * Convenience wrapper: take any caught error from an admin RPC call and
 * pull out the field/message pair if it's a typed ReferrerError. Returns
 * `null` for non-Referrer errors so callers can fall through to a generic
 * form-level error message.
 */
export function mapReferrerErrorToFieldError(
  err: unknown,
): FieldErrorMapping | null {
  if (
    typeof err !== "object" ||
    err === null ||
    !("code" in err) ||
    typeof (err as { code: unknown }).code !== "string"
  ) {
    return null;
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const code = (err as { code: string }).code;
  if (!isReferrerErrorCode(code)) {
    return null;
  }
  return mapReferrerErrorCodeToFieldError(code);
}

const REFERRER_ERROR_CODES: ReadonlySet<ReferrerErrorCode> = new Set([
  "referrer.auth.generic",
  "referrer.auth.magic_link_invalid",
  "referrer.auth.magic_link_already_used",
  "referrer.auth.referrer_not_found",
  "referrer.auth.referrer_terminated",
  "referrer.auth.referrer_paused",
  "referrer.referral_code.invalid",
  "referrer.referral_code.reserved",
  "referrer.referral_code.already_exists",
  "referrer.referral_code.generation_exhausted",
  "referrer.email.already_exists",
  "referrer.status.invalid_transition",
]);

function isReferrerErrorCode(code: string): code is ReferrerErrorCode {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return REFERRER_ERROR_CODES.has(code as ReferrerErrorCode);
}
